// Day picker for the faceted spa flow — a native <input type="date"> bounded to
// [today, booking horizon], flanked by prev/next-day toggles for one-tap
// stepping. The day (not a datetime) is the source of truth; each treatment's
// slots are fetched per day and the guest taps a real time chip below.

// Add `delta` calendar days to a 'YYYY-MM-DD' string. Pure UTC arithmetic so it
// never drifts across a DST boundary or the local tz — these are plain calendar
// dates, not ChiDesk fake-Z timestamps.
function addDays(ymd: string, delta: number): string {
  const parts = ymd.split("-");
  const y = Number(parts[0]);
  const m = Number(parts[1]);
  const d = Number(parts[2]);
  return new Date(Date.UTC(y, m - 1, d + delta)).toISOString().slice(0, 10);
}

export function DaySelector({
  day,
  min,
  max,
  onChange,
}: {
  day: string; // 'YYYY-MM-DD'
  min: string;
  max?: string;
  onChange: (day: string) => void;
}) {
  const canPrev = day > min;
  const canNext = !max || day < max;
  const shift = (delta: number) => {
    const next = addDays(day, delta);
    const clamped = next < min ? min : max && next > max ? max : next;
    if (clamped !== day) onChange(clamped);
  };
  return (
    <div className="vt-field vt-day-field">
      <label>Date</label>
      <div className="vt-day-control">
        <button
          type="button"
          className="vt-day-nav"
          aria-label="Previous day"
          disabled={!canPrev}
          onClick={() => shift(-1)}
        >
          &lsaquo;
        </button>
        <input
          type="date"
          value={day}
          min={min}
          max={max}
          onChange={(e) => {
            // `min`/`max` only soft-block the calendar UI — a typed or pasted
            // value below today still fires onChange, so re-clamp here.
            const raw = e.target.value;
            if (!raw) return;
            const clamped = raw < min ? min : max && raw > max ? max : raw;
            onChange(clamped);
          }}
        />
        <button
          type="button"
          className="vt-day-nav"
          aria-label="Next day"
          disabled={!canNext}
          onClick={() => shift(1)}
        >
          &rsaquo;
        </button>
      </div>
    </div>
  );
}
